import SEOHelmet from "../components/SEOHelmet";
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import OnboardingTour from '../components/OnboardingTour';
import EmployerQuickPost from '../components/EmployerQuickPost';
import { EmployerComplianceScore } from '../components/TrustBadges';
import BetaWelcomeModal from '../components/BetaWelcomeModal';
import LiveMapPage from './LiveMapPage';
import EmployerPortalManager from '../components/EmployerPortalManager';

const api = () => axios.create({
 baseURL: '/api',
 headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const INDUSTRIES = ['Hospitality','Construction','Retail','Events','Warehouse','Cleaning','Security','Admin','Other'];
const INDUSTRY_ICONS = {
 Hospitality:'', Construction:'', Retail:'', Events:'',
 Warehouse:'', Cleaning:'', Security:'', Admin:'', Other:'', General:'',
};
const PERFECT_FOR_OPTIONS = [
 'Students','First job','Career changers','Anyone','Hospitality beginners',
 'Retail newcomers','Physically fit','Night owls','Weekend warriors',
];

const STATUS_BADGE = {
 open: 'bg-brand-50 text-brand-700 border-brand-200',
 filled: 'bg-blue-50 text-blue-700 border-blue-200',
 closed: 'bg-neutral-100 text-neutral-500 border-neutral-200',
};
const APP_STATUS_BADGE = {
 pending: 'bg-amber-50 text-amber-700 border-amber-200',
 hired: 'bg-brand-50 text-brand-700 border-brand-200',
 declined: 'bg-red-50 text-red-600 border-red-200',
};

const CERT_OPTIONS = ['RSA Certificate', 'White Card (Construction)', 'Food Handler Certificate', 'First Aid Certificate', 'Security Licence', 'Working With Children Check (WWCC)', 'None required'];

const BLANK_FORM = {
 title: '', location: 'Sydney', industry: 'Hospitality',
 hourly_rate: '', hours: '', description: '',
 entry_level: true, what_youll_do: '', perfect_for_tags: [], required_certs: [],
 meal_provided: false, parking_available: false, public_transport_nearby: false,
 uniform_required: false, uniform_requirements: '', safety_instructions: '',
};

const TABS = [
 ['post', 'Post'],
 ['shifts', 'Shifts'],
 ['applicants','Hire'],
 ['crew', 'My Crew'],
 ['analytics', 'Stats'],
 ['livemap', '🗺️ Live'],
 ['portals', '🔗 Portals'],
];

export default function EmployerDashboard({ user, onLogout }) {
 const navigate = useNavigate();
 const http = api();
 const [shifts, setShifts] = useState([]);
 const [tab, setTab] = useState('post');
 const [form, setForm] = useState(BLANK_FORM);
 const [posting, setPosting] = useState(false);
 const [toast, setToast] = useState({ msg: '', type: 'ok' });
 const [loading, setLoading] = useState(true);
 const [showBetaModal, setShowBetaModal] = useState(() => !localStorage.getItem('beta_welcomed'));

 // Applicants tab state
 const [selectedShiftId, setSelectedShiftId] = useState('');
 const [applicants, setApplicants] = useState([]);
 const [loadingApps, setLoadingApps] = useState(false);
 const [actionLoading, setActionLoading] = useState(null);
 const [messagingApp, setMessagingApp] = useState(null);
 const [messageText, setMessageText] = useState('');
 const [sendingMsg, setSendingMsg] = useState(false);
 const [abnStatus, setAbnStatus] = useState(null);
 const [mobileMenu, setMobileMenu] = useState(false);
 const [complianceScore, setComplianceScore] = useState(100);
 const [useQuickPost, setUseQuickPost] = useState(false);

 const userData = user || JSON.parse(localStorage.getItem('user') || '{}');

 useEffect(() => { loadShifts(); loadABNStatus(); loadCompliance(); }, []);

 const loadCompliance = async () => {
 try {
 const res = await http.get('/legal/compliance-status');
 setComplianceScore(res.data.compliance_score || 0);
 } catch { /* silent */ }
 };

 const loadABNStatus = async () => {
 try {
 const res = await http.get('/employer/abn');
 setAbnStatus(res.data);
 } catch { /* silent */ }
 };

 const loadShifts = async () => {
 setLoading(true);
 try {
 const res = await http.get('/shifts');
 const all = res.data || [];
 const mine = all.filter(s =>
 s.employer_id === userData?.id || s.employer_email === userData?.email
 );
 setShifts(mine.length ? mine : all);
 } catch { /* silent */ }
 setLoading(false);
 };

 const showToast = (msg, type = 'ok') => {
 setToast({ msg, type });
 setTimeout(() => setToast({ msg: '', type: 'ok' }), 4500);
 };

 const handleLogout = () => {
 localStorage.removeItem('token');
 localStorage.removeItem('user');
 if (onLogout) onLogout();
 };

 const togglePerfectFor = (tag) => {
 const tags = form.perfect_for_tags.includes(tag)
 ? form.perfect_for_tags.filter(t => t !== tag)
 : [...form.perfect_for_tags, tag];
 setForm({ ...form, perfect_for_tags: tags });
 };

 const postShift = async (e) => {
 e.preventDefault();
 setPosting(true);
 try {
 const perfectForStr = form.perfect_for_tags.length
 ? form.perfect_for_tags.join(', ')
 : 'Students, First-timers, Anyone';
 await http.post('/shifts', {
 ...form,
 hourly_rate: parseFloat(form.hourly_rate),
 hours: parseFloat(form.hours),
 perfect_for: perfectForStr,
 what_youll_do: form.what_youll_do || form.description,
 required_certs: form.required_certs.filter(c => c !== 'None required'),
 });
 showToast(`✅ "${form.title}" is live! Workers are being notified now.`);
 setForm(BLANK_FORM);
 setTab('shifts');
 loadShifts();
 } catch (err) {
 showToast(err.response?.data?.error || 'Could not post shift.', 'err');
 }
 setPosting(false);
 };

 const loadApplicants = async (shiftId) => {
 if (!shiftId) return;
 setLoadingApps(true);
 try {
 const res = await http.get(`/shifts/${shiftId}/applications`);
 setApplicants(res.data || []);
 } catch { setApplicants([]); }
 setLoadingApps(false);
 };

 const handleShiftSelect = (shiftId) => {
 setSelectedShiftId(shiftId);
 loadApplicants(shiftId);
 };

 const handleAction = async (appId, action) => {
 setActionLoading(appId + action);
 try {
 await http.put(`/applications/${appId}/${action}`);
 showToast(action === 'hire' ? 'Worker hired! They\'ve been notified instantly.' : '✖ Application declined.');
 await loadApplicants(selectedShiftId);
 loadShifts();
 } catch (err) {
 showToast(err.response?.data?.error || 'Action failed.', 'err');
 }
 setActionLoading(null);
 };

 const openCount = shifts.filter(s => s.status === 'open').length;
 const hiredCount = shifts.reduce((n, s) => n + (s.hired_count || 0), 0);
 const applicantCount = shifts.reduce((n, s) => n + (s.application_count || 0), 0);
 const totalPay = (s) => (s.hourly_rate || 0) * (s.hours || 0);

 const selectedShift = shifts.find(s => String(s.id) === String(selectedShiftId));

 return (
 <div className="min-h-screen bg-neutral-50 pb-8">
 <SEOHelmet title="Employer Dashboard" description="Post shifts and hire workers on ShiftMatch." />
 <OnboardingTour role="employer" />
 {showBetaModal && (
 <BetaWelcomeModal role="employer" onDismiss={() => setShowBetaModal(false)} />
 )}
 {/* ── HEADER ── */}
 <div className="bg-white border-b border-neutral-200 shadow-sm sticky top-0 z-30">
 <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center justify-between">
 <div className="flex items-center gap-2">
 <img src="/logo-icon.svg" alt="" className="w-7 h-7" />
 <span className="font-brand font-bold text-neutral-900 text-sm">ShiftMatch</span>
 </div>
 {/* Desktop nav */}
 <div className="hidden sm:flex items-center gap-1.5">
 <span className="text-xs text-neutral-500 px-2"> {userData?.name?.split(' ')[0] || 'Employer'}</span>
 {userData?.verified && (
 <span className="text-[10px] px-2 py-0.5 bg-brand-50 text-brand-700 border border-brand-200 rounded-full font-semibold whitespace-nowrap">✓ Verified</span>
 )}
 <button onClick={() => navigate('/pricing')} className="text-xs text-neutral-500 hover:text-brand-600 px-2 py-1.5 rounded-lg hover:bg-brand-50"> Plans</button>
 <button onClick={() => navigate('/ability-hub')} className="text-xs text-violet-600 hover:text-violet-800 px-2 py-1.5 rounded-lg hover:bg-violet-50 font-semibold">♿ Inclusive Hiring</button>
 <button onClick={() => navigate('/settings')} className="text-xs text-neutral-500 hover:text-brand-600 px-2 py-1.5 rounded-lg hover:bg-brand-50"></button>
 <button onClick={handleLogout} className="text-xs text-red-400 hover:text-red-600 px-2 py-1.5 rounded-lg hover:bg-red-50 font-medium">Sign out</button>
 </div>
 {/* Mobile nav */}
 <div className="flex sm:hidden items-center gap-1.5">
 {userData?.verified && (
 <span className="text-[10px] px-1.5 py-0.5 bg-brand-50 text-brand-700 border border-brand-200 rounded-full font-semibold">✓</span>
 )}
 <button onClick={() => setMobileMenu(!mobileMenu)} className="p-2 rounded-lg hover:bg-neutral-100 text-neutral-600" aria-label="Menu">
 {mobileMenu ? '✕' : '☰'}
 </button>
 </div>
 </div>
 {/* Mobile dropdown */}
 {mobileMenu && (
 <div className="sm:hidden bg-white border-t border-neutral-100 px-4 py-3 space-y-1.5">
 <div className="text-sm text-neutral-700 font-medium pb-2 border-b border-neutral-100"> {userData?.name || 'Employer'} {userData?.verified && '✅ Verified Business'}</div>
 <button onClick={() => { navigate('/pricing'); setMobileMenu(false); }} className="w-full text-left text-sm text-neutral-600 py-2 hover:text-brand-600"> Upgrade Plan</button>
 <button onClick={() => { navigate('/insurance'); setMobileMenu(false); }} className="w-full text-left text-sm text-neutral-600 py-2 hover:text-brand-600"> Insurance</button>
 <button onClick={() => { navigate('/settings'); setMobileMenu(false); }} className="w-full text-left text-sm text-neutral-600 py-2 hover:text-brand-600"> Settings</button>
 <button onClick={handleLogout} className="w-full text-left text-sm text-red-500 py-2 font-medium border-t border-neutral-100 mt-2 pt-3">Sign out</button>
 </div>
 )}
 </div>

 <div className="max-w-5xl mx-auto px-4 py-6">

 {/* Compliance nudge */}
 {complianceScore < 80 && (
 <a href="/compliance" className="block mb-4 p-4 bg-blue-50 border border-blue-200 rounded-2xl hover:bg-blue-100 transition">
 <div className="flex items-center justify-between">
 <div>
 <p className="font-semibold text-blue-800 text-sm"> {complianceScore}% compliant</p>
 <p className="text-xs text-blue-600 mt-0.5">Complete your compliance checklist to unlock all features</p>
 </div>
 <span className="text-blue-600 font-bold">→</span>
 </div>
 </a>
 )}

 {/* ── ABILITY HUB PROMO ── */}
 <div className="mb-4 p-4 bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-2xl flex items-center justify-between gap-3">
 <div className="flex items-center gap-3">
 <span className="text-2xl">♿</span>
 <div>
 <p className="font-bold text-violet-900 text-sm">Earn up to $10,950 hiring workers with disability</p>
 <p className="text-xs text-violet-600 mt-0.5">Government subsidies + EAF grants + reduced ShiftMatch commission — see AbilityHub</p>
 </div>
 </div>
 <button onClick={() => window.location.href='/ability-hub'} className="text-xs bg-violet-600 text-white px-3 py-2 rounded-xl font-semibold hover:bg-violet-700 transition whitespace-nowrap">Learn More →</button>
 </div>

 {/* ── ABN BANNER ── */}
 {abnStatus && !abnStatus.verified && (
 <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between">
 <p className="text-sm text-amber-800">⚠️ <strong>Verify your ABN</strong> to get a trust badge and 3x more applicants</p>
 <button onClick={() => window.location.href='/abn-verification'} className="ml-4 text-xs bg-amber-600 text-white px-3 py-1.5 rounded-lg font-semibold hover:bg-amber-700 transition whitespace-nowrap">Verify Now →</button>
 </div>
 )}

 {/* ── STATS ── */}
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
 {[
 { label: 'Total Posted', value: shifts.length, icon: '', valClass: 'text-neutral-900' },
 { label: 'Open Now', value: openCount, icon: '', valClass: 'text-brand-600' },
 { label: 'Workers Hired', value: hiredCount, icon: '✅', valClass: 'text-brand-600' },
 { label: 'Total Applicants',value: applicantCount, icon: '', valClass: 'text-blue-600' },
 ].map(s => (
 <div key={s.label} className="bg-white rounded-2xl p-4 border border-neutral-100 shadow-sm">
 <div className="text-xl mb-1">{s.icon}</div>
 <div className={`text-2xl font-bold ${s.valClass}`}>{s.value}</div>
 <div className="text-xs text-neutral-400 mt-0.5">{s.label}</div>
 </div>
 ))}
 </div>

 {/* ── TRUST BANNER ── */}
 <div className="mb-4 p-3 sm:p-4 bg-brand-50 border border-brand-200 rounded-2xl flex items-center justify-between gap-3">
 <div className="min-w-0">
 <p className="text-brand-700 font-semibold text-sm">Post a shift, hire in minutes </p>
 <p className="text-xs text-brand-600 mt-0.5 leading-relaxed">
 Motivated workers ready to go · Only pay when hired (15%)
 </p>
 </div>
 <div className="text-2xl shrink-0"></div>
 </div>

 {/* ── TOAST ── */}
 {toast.msg && (
 <div className={`mb-4 p-3 rounded-xl text-sm border ${
 toast.type === 'err'
 ? 'bg-red-50 border-red-200 text-red-700'
 : 'bg-brand-50 border-brand-200 text-brand-700'
 }`}>{toast.msg}</div>
 )}

 {/* ── TABS ── */}
 <div className="flex gap-0.5 bg-white p-1 rounded-xl mb-5 border border-neutral-100 shadow-sm overflow-x-auto">
 {TABS.map(([id, label]) => (
 <button key={id} onClick={() => setTab(id)}
 className={`flex-1 py-2 text-xs font-semibold rounded-lg transition whitespace-nowrap px-2 min-w-0 ${
 tab === id
 ? 'bg-brand-500 text-white shadow-sm'
 : 'text-neutral-500 hover:text-neutral-800 hover:bg-neutral-50'
 }`}>
 {id === 'shifts' && shifts.length > 0 ? ` Shifts (${shifts.length})` : label}
 </button>
 ))}
 </div>

 {/* ══ POST FORM ══ */}
 {tab === 'post' && (
 <div>
   {/* Quick post toggle */}
   <div className="mb-5 flex items-center justify-between">
     <h2 className="text-lg font-bold text-neutral-900">Post a Shift</h2>
     <button
       onClick={() => setUseQuickPost(!useQuickPost)}
       className="text-xs font-semibold px-3 py-1.5 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-all"
     >
       {useQuickPost ? 'Full Form' : '⚡ Quick Post'}
     </button>
   </div>

   {useQuickPost ? (
     <EmployerQuickPost onShiftCreated={(newShift) => { loadShifts(); setUseQuickPost(false); showToast('Shift posted!', 'ok'); }} onCancel={() => setUseQuickPost(false)} />
   ) : (
   <div className="bg-white rounded-2xl p-6 border border-neutral-100 shadow-sm">
 <p className="text-sm text-neutral-500 mb-5">Be specific — workers apply faster when they know exactly what to expect.</p>

 <form onSubmit={postShift} className="space-y-5">
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 <div className="sm:col-span-2">
 <label className="block text-sm font-medium text-neutral-700 mb-1.5">Shift Title *</label>
 <input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
 className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-neutral-900 placeholder-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition"
 placeholder="e.g. Barista — Saturday Brunch Service" />
 </div>
 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-1.5">Industry *</label>
 <select required value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })}
 className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-neutral-900 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition">
 {INDUSTRIES.map(i => <option key={i} value={i}>{INDUSTRY_ICONS[i]} {i}</option>)}
 </select>
 </div>
 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-1.5">Location *</label>
 <input required value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
 className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-neutral-900 placeholder-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition"
 placeholder="e.g. Surry Hills, Sydney" />
 </div>
 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-1.5">Hourly Rate (AUD$) *</label>
 <input required type="number" min="15" step="0.5" value={form.hourly_rate} onChange={e => setForm({ ...form, hourly_rate: e.target.value })}
 className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-neutral-900 placeholder-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition"
 placeholder="e.g. 28" />
 </div>
 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-1.5">Total Hours *</label>
 <input required type="number" min="1" max="24" step="0.5" value={form.hours} onChange={e => setForm({ ...form, hours: e.target.value })}
 className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-neutral-900 placeholder-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition"
 placeholder="e.g. 8" />
 </div>
 </div>

 {/* Live pay preview */}
 {form.hourly_rate && form.hours && (
 <div className="p-4 bg-brand-50 border border-brand-200 rounded-xl flex items-center justify-between">
 <span className="text-neutral-600 text-sm">Workers will see this total pay:</span>
 <span className="text-brand-600 font-bold text-lg">
 ↑ ${(parseFloat(form.hourly_rate) || 0) * (parseFloat(form.hours) || 0)} total
 <span className="text-neutral-400 font-normal text-sm ml-1">({form.hours}h × ${form.hourly_rate}/hr)</span>
 </span>
 </div>
 )}

 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-1.5">
 What You'll Do <span className="text-neutral-400">(plain English, no jargon)</span>
 </label>
 <textarea rows={2} value={form.what_youll_do} onChange={e => setForm({ ...form, what_youll_do: e.target.value })}
 className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-neutral-900 placeholder-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition resize-none"
 placeholder="e.g. Take orders, make coffees, clear tables. No experience necessary — we'll show you." />
 </div>

 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-1.5">Additional Details</label>
 <textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
 className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-neutral-900 placeholder-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition resize-none"
 placeholder="Dress code, start time, parking, anything else workers should know" />
 </div>

 {/* Perfect for tags */}
 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-2">
 Perfect for <span className="text-neutral-400">(select all that apply)</span>
 </label>
 <div className="flex flex-wrap gap-2">
 {PERFECT_FOR_OPTIONS.map(tag => (
 <button key={tag} type="button" onClick={() => togglePerfectFor(tag)}
 className={`px-3 py-1.5 rounded-lg text-sm font-medium transition border ${
 form.perfect_for_tags.includes(tag)
 ? 'bg-blue-50 border-blue-300 text-blue-700'
 : 'bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300'
 }`}>
 {tag}
 </button>
 ))}
 </div>
 </div>

 {/* Entry level toggle */}
 <div className="p-4 bg-neutral-50 rounded-xl border border-neutral-100 flex items-center justify-between">
 <div>
 <p className="text-neutral-900 font-semibold text-sm">This shift welcomes first-timers</p>
 <p className="text-xs text-neutral-500 mt-0.5">Workers with no experience can apply. Recommended — you'll get more applicants.</p>
 </div>
 <button
 type="button"
 onClick={() => setForm({ ...form, entry_level: !form.entry_level })}
 className={`relative w-12 h-6 rounded-full transition-colors ${form.entry_level ? 'bg-brand-500' : 'bg-neutral-300'}`}
 >
 <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.entry_level ? 'translate-x-7' : 'translate-x-1'}`} />
 </button>
 </div>

 {/* Required Certifications */}
 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-2">Required Certifications</label>
 <div className="flex flex-wrap gap-2">
 {CERT_OPTIONS.map(cert => (
 <button key={cert} type="button"
 onClick={() => {
 const certs = form.required_certs.includes(cert)
 ? form.required_certs.filter(c => c !== cert)
 : (cert === 'None required' ? ['None required'] : form.required_certs.filter(c => c !== 'None required').concat(cert));
 setForm({ ...form, required_certs: certs });
 }}
 className={`text-xs px-3 py-1.5 rounded-full border font-medium transition ${
 form.required_certs.includes(cert)
 ? 'bg-brand-600 text-white border-brand-600'
 : 'bg-white text-neutral-600 border-neutral-300 hover:border-brand-400'
 }`}>
 {cert}
 </button>
 ))}
 </div>
 </div>

 {/* Perks */}
 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-2">Perks &amp; Amenities</label>
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
 {[
 { key: 'meal_provided', label: ' Meal provided', icon: '' },
 { key: 'parking_available', label: ' Free parking', icon: '' },
 { key: 'public_transport_nearby', label: ' PT nearby', icon: '' },
 { key: 'uniform_required', label: ' Uniform provided', icon: '' },
 ].map(perk => (
 <button key={perk.key} type="button"
 onClick={() => setForm({ ...form, [perk.key]: !form[perk.key] })}
 className={`text-xs px-3 py-2.5 rounded-xl border font-medium transition ${
 form[perk.key]
 ? 'bg-brand-50 text-brand-700 border-brand-300'
 : 'bg-white text-neutral-500 border-neutral-200 hover:border-brand-300'
 }`}>
 {perk.label}
 </button>
 ))}
 </div>
 </div>

 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-1.5">Uniform Requirements</label>
 <input value={form.uniform_requirements || ''} onChange={e => setForm({ ...form, uniform_requirements: e.target.value })}
 className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-neutral-900 placeholder-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition"
 placeholder="e.g. Black shirt and closed-toe shoes" />
 </div>

 <div>
 <label className="block text-sm font-medium text-neutral-700 mb-1.5">Safety Instructions</label>
 <textarea rows={2} value={form.safety_instructions || ''} onChange={e => setForm({ ...form, safety_instructions: e.target.value })}
 className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-neutral-900 placeholder-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition resize-none"
 placeholder="e.g. Emergency exits are located at the rear. Safety equipment is provided on site." />
 </div>

 {form.entry_level && (
 <div className="flex items-center gap-2 text-sm text-brand-700 bg-brand-50 border border-brand-200 rounded-xl px-4 py-3">
 <span></span>
 <span>Workers will see <strong>"✓ No experience needed"</strong> on your shift — this increases applications significantly.</span>
 </div>
 )}

 <button type="submit" disabled={posting}
 className="w-full py-3.5 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-xl transition disabled:opacity-50 text-base shadow-sm">
 {posting ? 'Posting…' : ' Post Shift — Workers Notified Instantly'}
 </button>

 <p className="text-center text-xs text-neutral-400">✓ You only pay when a worker is hired (15% commission — fair and transparent)</p>
 </form>
 </div>
   )}
 </div>
 )}

 {/* ══ MY POSTED SHIFTS ══ */}
 {tab === 'shifts' && (
 <div className="space-y-3">
 {loading ? (
 <div className="flex justify-center py-16">
 <div className="w-7 h-7 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
 </div>
 ) : shifts.length === 0 ? (
 <div className="text-center text-neutral-400 py-16 bg-white rounded-2xl border border-neutral-100 shadow-sm">
 <div className="text-4xl mb-3"></div>
 <p className="font-medium text-neutral-600">No shifts posted yet</p>
 <button onClick={() => setTab('post')}
 className="mt-4 px-5 py-2.5 bg-brand-500 text-white font-bold rounded-xl text-sm hover:bg-brand-600 transition">
 Post Your First Shift
 </button>
 </div>
 ) : shifts.map(shift => {
 const pay = totalPay(shift);
 const industry = shift.industry || 'Other';
 const perfectFor = shift.perfect_for
 ? shift.perfect_for.split(',').map(s => s.trim()).filter(Boolean)
 : [];
 return (
 <div key={shift.id} className="bg-white rounded-2xl p-5 border border-neutral-100 shadow-sm hover:shadow-md hover:border-brand-200 transition">
 <div className="flex items-start justify-between gap-4">
 <div className="flex-1">
 <div className="flex items-center gap-2 flex-wrap mb-2">
 <h3 className="font-bold text-neutral-900">{shift.title}</h3>
 <span className="text-xs px-2 py-0.5 bg-neutral-100 text-neutral-500 rounded-full">
 {INDUSTRY_ICONS[industry] || ''} {industry}
 </span>
 <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${STATUS_BADGE[shift.status] || STATUS_BADGE.open}`}>
 {(shift.status || 'open').toUpperCase()}
 </span>
 {shift.entry_level && (
 <span className="text-xs px-2 py-0.5 bg-brand-50 text-brand-700 border border-brand-200 rounded-full">✓ Entry-level</span>
 )}
 {Array.isArray(shift.required_certs) && shift.required_certs.map(cert => (
 <span key={cert} className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full"> {cert}</span>
 ))}
 {shift.meal_provided && <span className="text-xs px-2 py-0.5 bg-brand-50 text-brand-700 border border-brand-200 rounded-full"> Meal</span>}
 {shift.parking_available && <span className="text-xs px-2 py-0.5 bg-brand-50 text-brand-700 border border-brand-200 rounded-full"> Parking</span>}
 {shift.public_transport_nearby && <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-full"> PT nearby</span>}
 </div>
 <div className="flex flex-wrap gap-3 text-sm text-neutral-500 mb-2">
 <span> {shift.location || 'Sydney'}</span>
 <span> {shift.hours}h</span>
 <span>${shift.hourly_rate}/hr</span>
 {pay > 0 && <span className="text-brand-600 font-bold">↑ ${pay.toFixed(0)} total</span>}
 {shift.application_count > 0 && (
 <span className="text-blue-600 font-medium">{shift.application_count} applicant{shift.application_count !== 1 ? 's' : ''}</span>
 )}
 </div>
 {perfectFor.length > 0 && (
 <div className="flex flex-wrap gap-1">
 {perfectFor.slice(0, 4).map(t => (
 <span key={t} className="text-xs px-2 py-0.5 bg-blue-50 text-blue-600 rounded-full border border-blue-100">{t}</span>
 ))}
 </div>
 )}
 </div>
 <div className="flex flex-col gap-1.5 shrink-0">
 <button
 onClick={() => { setSelectedShiftId(String(shift.id)); setTab('applicants'); loadApplicants(shift.id); }}
 className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white font-semibold text-xs rounded-xl transition shadow-sm"
 >
 Review{shift.application_count > 0 ? ` ${shift.application_count}` : ''}
 </button>
 <button
 onClick={async () => {
 try {
 const r = await http.post(`/shifts/${shift.id}/repost`, { date: new Date(Date.now() + 7*86400000).toISOString().split('T')[0] });
 showToast('Shift reposted for next week');
 loadShifts();
 } catch { showToast('Failed to repost', 'err'); }
 }}
 className="px-4 py-1.5 border border-neutral-200 text-neutral-600 hover:bg-brand-50 hover:text-brand-700 hover:border-brand-200 font-medium text-xs rounded-xl transition"
 >
 Repost
 </button>
 {!shift.is_urgent && (
 <button
 onClick={async () => {
 try {
 await http.put(`/shifts/${shift.id}/urgent`, { urgency_reason: 'Urgent hire needed' });
 showToast(' Shift marked urgent! Workers will see it in "Need You Now"');
 loadShifts();
 } catch { showToast('Failed', 'err'); }
 }}
 className="px-4 py-1.5 border border-red-200 text-red-500 hover:bg-red-50 font-medium text-xs rounded-xl transition"
 >
 Mark Urgent
 </button>
 )}
 {shift.is_urgent && (
 <span className="px-3 py-1 bg-red-50 text-red-600 border border-red-200 text-[10px] rounded-lg font-bold"> URGENT</span>
 )}
 <div className="flex gap-2 text-[10px] text-neutral-400 flex-wrap">
 {shift.view_count > 0 && <span> {shift.view_count} views</span>}
 {shift.status === 'filled' && (
 <a
 href={`/api/hires/shift-${shift.id}/proof-pdf`}
 target="_blank"
 rel="noopener noreferrer"
 className="text-brand-500 hover:text-brand-700 font-semibold"
 title="Download Shift Proof PDF"
 >
 📄 Proof PDF
 </a>
 )}
 </div>
 </div>
 </div>
 </div>
 );
 })}
 </div>
 )}

 {/* ══ REVIEW APPLICANTS ══ */}
 {tab === 'applicants' && (
 <div>
 {/* Shift selector */}
 <div className="bg-white rounded-2xl p-5 border border-neutral-100 shadow-sm mb-4">
 <label className="block text-sm font-medium text-neutral-700 mb-2">Select a shift to review applicants</label>
 <select
 value={selectedShiftId}
 onChange={e => handleShiftSelect(e.target.value)}
 className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-neutral-900 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition"
 >
 <option value="">— Choose a shift —</option>
 {shifts.map(s => (
 <option key={s.id} value={String(s.id)}>
 {s.title} · {s.location} · ${totalPay(s)} total{s.application_count > 0 ? ` · ${s.application_count} applicant${s.application_count !== 1 ? 's' : ''}` : ''}
 </option>
 ))}
 </select>
 </div>

 {/* Selected shift info */}
 {selectedShift && (
 <div className="bg-brand-50 border border-brand-200 rounded-2xl px-5 py-3 mb-4 flex flex-wrap gap-4 items-center">
 <div>
 <span className="font-bold text-neutral-900">{selectedShift.title}</span>
 <span className="ml-2 text-sm text-neutral-500">
 {INDUSTRY_ICONS[selectedShift.industry] || ''} {selectedShift.industry} · {selectedShift.hours}h ·&nbsp;
 <span className="text-brand-600 font-bold">${totalPay(selectedShift)} total</span>
 </span>
 </div>
 {selectedShift.entry_level && (
 <span className="text-xs px-2 py-0.5 bg-brand-100 text-brand-700 border border-brand-300 rounded-full">✓ Entry-level</span>
 )}
 </div>
 )}

 {/* Applicants list */}
 {!selectedShiftId ? (
 <div className="text-center text-neutral-400 py-16 bg-white rounded-2xl border border-neutral-100 shadow-sm">
 <div className="text-4xl mb-3"></div>
 <p className="font-medium text-neutral-600">Select a shift above to see applicants</p>
 </div>
 ) : loadingApps ? (
 <div className="flex justify-center py-16">
 <div className="w-7 h-7 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
 </div>
 ) : applicants.length === 0 ? (
 <div className="text-center text-neutral-400 py-16 bg-white rounded-2xl border border-neutral-100 shadow-sm">
 <div className="text-4xl mb-3"></div>
 <p className="font-medium text-neutral-600">No applicants yet for this shift</p>
 <p className="text-sm mt-1 text-neutral-400">Your shift is live — workers can apply now</p>
 </div>
 ) : (
 <div className="space-y-3">
 {applicants.map(app => {
 const isBusy = actionLoading === app.id + 'hire' || actionLoading === app.id + 'decline';
 const isHired = app.status === 'hired';
 const isDeclined = app.status === 'declined';
 return (
 <div key={app.id} className="bg-white rounded-2xl p-5 border border-neutral-100 shadow-sm">
 <div className="flex items-center justify-between gap-4">
 <div className="flex items-center gap-4">
 <div className="w-11 h-11 rounded-full bg-brand-100 flex items-center justify-center font-bold text-brand-700 text-lg shrink-0">
 {(app.worker_name || 'W').charAt(0).toUpperCase()}
 </div>
 <div>
 <p className="font-semibold text-neutral-900">{app.worker_name || 'Worker'}</p>
 <p className="text-xs text-neutral-400">{app.worker_email}</p>
 <p className="text-xs text-neutral-300 mt-0.5">
 Applied {new Date(app.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
 </p>
 </div>
 </div>
 <div className="flex flex-col gap-2 items-end shrink-0">
 {(isHired || isDeclined) ? (
 <>
 <span className={`text-xs px-3 py-1.5 rounded-full border font-bold ${APP_STATUS_BADGE[app.status]}`}>
 {app.status === 'hired' ? '✓ HIRED' : 'DECLINED'}
 </span>
 {isHired && (
 <>
 <button
 onClick={() => setMessagingApp(messagingApp === app.id ? null : app.id)}
 className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold text-xs rounded-lg transition border border-blue-200">
 Message Worker
 </button>
 <button
 disabled={actionLoading === app.id + 'noshow'}
 onClick={async () => {
 setActionLoading(app.id + 'noshow');
 try { await http.put(`/applications/${app.id}/noshow`); showToast(' No-show reported.'); await loadApplicants(selectedShiftId); } catch { showToast('Failed', 'err'); }
 setActionLoading(null);
 }}
 className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 font-semibold text-xs rounded-lg transition border border-red-200 disabled:opacity-50">
 {actionLoading === app.id + 'noshow' ? '…' : ' Report No-show'}
 </button>
 <div className="flex gap-1.5 mt-0.5 flex-wrap">
 <button
 onClick={async () => {
 try {
 await http.post('/employer/trusted-workers', { worker_id: app.worker_id, tier: 'trusted', notes: `Added after ${shifts.find(s=>s.id===selectedShiftId)?.title || 'shift'}` });
 showToast(` ${app.worker_name} added to your crew!`);
 } catch { showToast('Already in crew or failed', 'err'); }
 }}
 className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold text-[10px] rounded-lg transition border border-blue-200">
 Add to Crew
 </button>
 <a href={`/reviews/${app.worker_id}`}
 className="px-2.5 py-1 bg-amber-50 hover:bg-amber-100 text-amber-700 font-semibold text-[10px] rounded-lg transition border border-amber-200">
 Review
 </a>
 <a href={`/report?type=worker&userId=${app.worker_id}&name=${encodeURIComponent(app.worker_name || 'Worker')}`}
 className="px-2.5 py-1 bg-red-50 hover:bg-red-100 text-red-500 font-semibold text-[10px] rounded-lg transition border border-red-200">
 Report
 </a>
 </div>
 {messagingApp === app.id && (
 <div className="mt-1 w-56">
 <textarea rows={2} value={messageText} onChange={e => setMessageText(e.target.value)}
 placeholder="Type a message..."
 className="w-full border border-neutral-200 rounded-xl px-3 py-2 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none" />
 <button disabled={sendingMsg || !messageText.trim()}
 onClick={async () => {
 setSendingMsg(true);
 try {
 await http.post('/messages', { shift_id: selectedShiftId, recipient_id: app.worker_id, content: messageText });
 setMessageText(''); setMessagingApp(null);
 showToast(' Message sent!');
 } catch { showToast('Failed to send', 'err'); }
 setSendingMsg(false);
 }}
 className="mt-1 w-full px-3 py-1.5 bg-brand-500 hover:bg-brand-600 text-white font-bold text-xs rounded-lg transition disabled:opacity-50">
 {sendingMsg ? 'Sending…' : 'Send Message'}
 </button>
 </div>
 )}
 </>
 )}
 </>
 ) : (
 <div className="flex gap-2">
 <button
 disabled={isBusy}
 onClick={() => handleAction(app.id, 'hire')}
 className="px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white font-bold text-xs rounded-lg transition disabled:opacity-50 shadow-sm"
 >
 {actionLoading === app.id + 'hire' ? '…' : '✓ Hire'}
 </button>
 <button
 disabled={isBusy}
 onClick={() => handleAction(app.id, 'decline')}
 className="px-4 py-2 bg-neutral-100 hover:bg-red-50 text-neutral-600 hover:text-red-600 font-bold text-xs rounded-lg transition disabled:opacity-50 border border-neutral-200 hover:border-red-200"
 >
 {actionLoading === app.id + 'decline' ? '…' : 'Decline'}
 </button>
 </div>
 )}
 </div>
 </div>
 </div>
 );
 })}
 </div>
 )}
 </div>
 )}

 {/* ══ ANALYTICS TAB ══ */}
 {/* ══ MY CREW TAB ══ */}
 {tab === 'crew' && <TrustedCrewPanel http={http} shifts={shifts} showToast={showToast} />}

 {tab === 'analytics' && <EmployerAnalytics http={http} />}

 {tab === 'livemap' && <LiveMapPage onBack={() => setTab('shifts')} />}

 {tab === 'portals' && <EmployerPortalManager shifts={shifts} />}
 </div>
 <footer className="text-center py-6 text-xs text-neutral-400 border-t border-neutral-100 mt-8">
 © 2026 ShiftMatch Australia ·{' '}
 <a href="/terms.html" target="_blank" className="hover:text-brand-600 mx-1">Terms</a>·
 <a href="/privacy.html" target="_blank" className="hover:text-brand-600 mx-1">Privacy</a>·
 <a href="mailto:support@shiftmatch.work" className="hover:text-brand-600 mx-1">Support</a>
 </footer>
 </div>
 );
}

// Employer Analytics Component
function EmployerAnalytics({ http }) {
 const [data, setData] = React.useState(null);
 const [loading, setLoading] = React.useState(true);

 React.useEffect(() => {
 const load = async () => {
 try { const r = await http.get('/employer/analytics'); setData(r.data); } catch { /* silent */ }
 setLoading(false);
 };
 load();
 }, []);

 if (loading) return <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>;
 if (!data) return <p className="text-center text-neutral-400 py-8">Could not load analytics</p>;

 const maxWeeklyHires = data.weekly_trend?.length > 0
 ? Math.max(...data.weekly_trend.map(w => w.hires), 1)
 : 1;

 // Auto-generate tips based on actual metrics
 const insights = [];
 if (data.time_to_fill_hours != null && data.time_to_fill_hours > 48)
 insights.push('⏱ Tip: Your average fill time is ' + data.time_to_fill_hours + 'h — employers who respond within 2hrs get 3× more quality hires');
 if (data.avg_response_time_hours != null && data.avg_response_time_hours > 4)
 insights.push('💬 Tip: Faster responses win — try to reply to applicants within 2 hours of posting');
 if (data.repeat_worker_rate < 20 && data.total_hires > 3)
 insights.push('🔄 Tip: Build your Trusted Crew — repeat workers are 40% more reliable and fill faster');
 if (data.hire_rate < 30 && data.total_applications > 5)
 insights.push('📋 Tip: Low hire rate detected — add entry-level tags and meal/parking perks to attract more suitable applicants');
 if (insights.length === 0) {
 insights.push('✅ Your hiring metrics look healthy — keep responding quickly and reviewing workers after shifts');
 if (data.total_views < 20) insights.push('📣 Tip: Share your shift links on local community Facebook groups to boost visibility');
 else insights.push('⭐ Tip: Employers with 4.5+ ratings fill shifts 2× faster — always leave a worker review after each shift');
 }

 return (
 <div className="space-y-4">
 <h2 className="text-lg font-bold text-neutral-900">📊 Your Analytics</h2>

 {/* Core stats */}
 <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
 {[
 { label: 'Shifts Posted', value: data.total_shifts, icon: '📋', cls: 'text-neutral-900' },
 { label: 'Total Views', value: data.total_views, icon: '👁', cls: 'text-blue-600' },
 { label: 'Applications', value: data.total_applications, icon: '📩', cls: 'text-amber-600' },
 { label: 'Hires Made', value: data.total_hires, icon: '✅', cls: 'text-brand-600' },
 ].map(s => (
 <div key={s.label} className="bg-white rounded-xl p-4 border border-neutral-100 shadow-sm text-center">
 <div className="text-xl mb-1">{s.icon}</div>
 <div className={`text-xl font-bold ${s.cls}`}>{s.value}</div>
 <div className="text-[10px] text-neutral-400">{s.label}</div>
 </div>
 ))}
 </div>

 {/* Performance stats */}
 <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
 <div className="bg-white rounded-xl p-4 border border-neutral-100 shadow-sm">
 <p className="text-xs text-neutral-400 mb-1">Hire Rate</p>
 <p className="text-2xl font-black text-brand-600">{data.hire_rate}%</p>
 <p className="text-[10px] text-neutral-400 mt-1">of applicants get hired</p>
 </div>
 <div className="bg-white rounded-xl p-4 border border-neutral-100 shadow-sm">
 <p className="text-xs text-neutral-400 mb-1">Your Rating</p>
 <p className="text-2xl font-black text-amber-500">{data.avg_rating > 0 ? `${data.avg_rating} ⭐` : '—'}</p>
 <p className="text-[10px] text-neutral-400 mt-1">{data.review_count} review{data.review_count !== 1 ? 's' : ''}</p>
 </div>
 <div className="bg-white rounded-xl p-4 border border-neutral-100 shadow-sm">
 <p className="text-xs text-neutral-400 mb-1">Time to Fill</p>
 <p className="text-2xl font-black text-blue-600">{data.time_to_fill_hours != null ? data.time_to_fill_hours + 'h' : '—'}</p>
 <p className="text-[10px] text-neutral-400 mt-1">avg hrs to first hire</p>
 </div>
 <div className="bg-white rounded-xl p-4 border border-neutral-100 shadow-sm">
 <p className="text-xs text-neutral-400 mb-1">Cost Per Hire</p>
 <p className="text-2xl font-black text-emerald-600">{data.cost_per_hire != null ? `$${data.cost_per_hire}` : '—'}</p>
 <p className="text-[10px] text-neutral-400 mt-1">avg labour cost</p>
 </div>
 <div className="bg-white rounded-xl p-4 border border-neutral-100 shadow-sm">
 <p className="text-xs text-neutral-400 mb-1">Repeat Workers</p>
 <p className="text-2xl font-black text-violet-600">{data.repeat_worker_rate}%</p>
 <p className="text-[10px] text-neutral-400 mt-1">of your hires return</p>
 </div>
 <div className="bg-white rounded-xl p-4 border border-neutral-100 shadow-sm">
 <p className="text-xs text-neutral-400 mb-1">Avg Response</p>
 <p className="text-2xl font-black text-orange-500">{data.avg_response_time_hours != null ? data.avg_response_time_hours + 'h' : '—'}</p>
 <p className="text-[10px] text-neutral-400 mt-1">time to respond</p>
 </div>
 </div>

 {/* Performance vs Platform */}
 <div className="bg-white rounded-xl p-4 border border-neutral-100 shadow-sm">
 <p className="text-xs font-bold text-neutral-700 mb-3">📈 Your Hire Rate vs Platform Average</p>
 <div className="flex items-center gap-3 mb-1">
 <span className="text-xs text-neutral-500 w-20">You</span>
 <div className="flex-1 bg-neutral-100 rounded-full h-3">
 <div
 className="bg-brand-500 h-3 rounded-full transition-all"
 style={{ width: `${Math.min(data.hire_rate, 100)}%` }}
 />
 </div>
 <span className="text-xs font-bold text-brand-600 w-10 text-right">{data.hire_rate}%</span>
 </div>
 <div className="flex items-center gap-3">
 <span className="text-xs text-neutral-500 w-20">Platform</span>
 <div className="flex-1 bg-neutral-100 rounded-full h-3">
 <div
 className="bg-neutral-400 h-3 rounded-full transition-all"
 style={{ width: `${Math.min(data.platform_avg_hire_rate, 100)}%` }}
 />
 </div>
 <span className="text-xs font-bold text-neutral-500 w-10 text-right">{data.platform_avg_hire_rate}%</span>
 </div>
 </div>

 {/* Weekly Trend — CSS bar chart */}
 {data.weekly_trend?.length > 0 && (
 <div className="bg-white rounded-xl p-4 border border-neutral-100 shadow-sm">
 <p className="text-xs font-bold text-neutral-700 mb-4">📅 Weekly Trend (last 4 weeks)</p>
 <div className="flex items-end gap-2 h-20">
 {data.weekly_trend.map((w, i) => (
 <div key={i} className="flex-1 flex flex-col items-center gap-1">
 <div
 className="w-full bg-brand-100 rounded-t-lg flex items-end justify-center"
 style={{ height: `${(w.hires / maxWeeklyHires) * 60 + 4}px`, minHeight: '4px' }}
 >
 <span className="text-[9px] font-bold text-brand-700">{w.hires > 0 ? w.hires : ''}</span>
 </div>
 <span className="text-[9px] text-neutral-400 whitespace-nowrap">{w.week}</span>
 </div>
 ))}
 </div>
 <div className="flex justify-between mt-1">
 {data.weekly_trend.map((w, i) => (
 <div key={i} className="flex-1 text-center">
 <p className="text-[9px] text-neutral-400">{w.applications} apps</p>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Insights */}
 <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
 <h3 className="font-bold text-blue-800 text-sm mb-2">💡 Insights</h3>
 <ul className="text-xs text-blue-700 space-y-1.5">
 {insights.map((tip, i) => <li key={i}>{tip}</li>)}
 </ul>
 </div>
 </div>
 );
}

// Trusted Crew Panel
function TrustedCrewPanel({ http, shifts, showToast }) {
 const [crew, setCrew] = React.useState([]);
 const [available, setAvailable] = React.useState([]);
 const [loading, setLoading] = React.useState(true);
 const [offerShift, setOfferShift] = React.useState(null);
 const [offerMsg, setOfferMsg] = React.useState('');
 const [sending, setSending] = React.useState(false);

 const TIER_BADGE = {
 top_pick: { label: ' Top Pick', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
 preferred: { label: ' Preferred', cls: 'bg-blue-50 text-blue-700 border-blue-200' },
 trusted: { label: '✅ Trusted', cls: 'bg-brand-50 text-brand-700 border-brand-200' },
 };

 React.useEffect(() => { loadCrew(); }, []);

 const loadCrew = async () => {
 try {
 const [c, a] = await Promise.all([
 http.get('/employer/trusted-workers'),
 http.get('/employer/trusted-workers/available').catch(() => ({ data: [] }))
 ]);
 setCrew(c.data || []);
 setAvailable(a.data || []);
 } catch { /* silent */ }
 setLoading(false);
 };

 const updateTier = async (workerId, tier) => {
 try { await http.put(`/employer/trusted-workers/${workerId}`, { tier }); loadCrew(); showToast('Updated'); } catch { /* silent */ }
 };

 const removeWorker = async (workerId) => {
 try { await http.delete(`/employer/trusted-workers/${workerId}`); loadCrew(); showToast('Removed'); } catch { /* silent */ }
 };

 const sendOffer = async (shiftId) => {
 setSending(true);
 try {
 const res = await http.post(`/shifts/${shiftId}/offer-trusted`, { message: offerMsg });
 showToast(res.data.message || '✅ Offers sent!');
 setOfferShift(null); setOfferMsg('');
 } catch (err) { showToast(err.response?.data?.error || 'Failed', 'err'); }
 setSending(false);
 };

 if (loading) return <div className="flex justify-center py-16"><div className="w-7 h-7 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>;

 return (
 <div className="space-y-5">
 <div className="p-4 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl text-white">
 <h2 className="text-lg font-bold mb-1"> Your Trusted Crew</h2>
 <p className="text-blue-200 text-xs leading-relaxed">
 Pre-approve workers you trust. When you need someone urgently, offer the shift to your crew first — they get a private notification and can accept instantly. No more gambling on strangers.
 </p>
 </div>

 {available.length > 0 && (
 <div className="bg-brand-50 border border-brand-200 rounded-2xl p-4">
 <div className="flex items-center gap-2 mb-2">
 <span className="inline-block w-2 h-2 bg-brand-500 rounded-full animate-pulse" />
 <h3 className="font-bold text-brand-800 text-sm">Available right now</h3>
 </div>
 <div className="flex flex-wrap gap-2">
 {available.map(w => (
 <div key={w.worker_id} className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-brand-200">
 <div className="w-7 h-7 rounded-full bg-brand-100 flex items-center justify-center text-[10px] font-bold text-brand-700">
 {(w.name || '?')[0]}
 </div>
 <div>
 <p className="text-xs font-bold text-neutral-900">{w.name}</p>
 <p className="text-[9px] text-neutral-400">{w.certs || 'No certs'}</p>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 <div className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-4">
 <h3 className="font-bold text-neutral-900 text-sm mb-2"> Offer a shift to your crew first</h3>
 <p className="text-xs text-neutral-500 mb-3">They get 2 hours to accept before it goes public. If someone accepts, they're auto-hired — no applications, no waiting.</p>
 {offerShift ? (
 <div className="space-y-2">
 <p className="text-xs text-neutral-700 font-medium">Offering: {shifts.find(s => s.id === offerShift)?.title || 'Shift'}</p>
 <textarea value={offerMsg} onChange={e => setOfferMsg(e.target.value)}
 placeholder="Add a personal message (optional)..."
 className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-xs resize-none h-16" />
 <div className="flex gap-2">
 <button onClick={() => sendOffer(offerShift)} disabled={sending}
 className="px-4 py-2 bg-brand-500 text-white font-bold text-xs rounded-lg hover:bg-brand-600 disabled:opacity-50">
 {sending ? 'Sending...' : ` Send to ${crew.length} worker${crew.length !== 1 ? 's' : ''}`}
 </button>
 <button onClick={() => setOfferShift(null)} className="px-4 py-2 border border-neutral-200 text-neutral-500 text-xs rounded-lg">Cancel</button>
 </div>
 </div>
 ) : (
 <select onChange={e => e.target.value && setOfferShift(e.target.value)} value=""
 className="w-full px-3 py-2.5 border border-neutral-200 rounded-lg text-sm text-neutral-600 bg-white">
 <option value="">Select a shift to offer...</option>
 {shifts.filter(s => s.status === 'open').map(s => (
 <option key={s.id} value={s.id}>{s.title} — ${s.hourly_rate}/hr</option>
 ))}
 </select>
 )}
 </div>

 <div>
 <h3 className="font-bold text-neutral-900 text-sm mb-3"> Your crew ({crew.length})</h3>
 {crew.length === 0 ? (
 <div className="text-center py-10 bg-white rounded-2xl border border-neutral-100">
 <div className="text-4xl mb-3"></div>
 <p className="font-medium text-neutral-600">No trusted workers yet</p>
 <p className="text-xs text-neutral-400 mt-1 max-w-xs mx-auto">After hiring someone great, add them to your crew from the applicant screen. Next time, they'll be first in line.</p>
 </div>
 ) : (
 <div className="space-y-2">
 {crew.map(w => {
 const badge = TIER_BADGE[w.tier] || TIER_BADGE.trusted;
 return (
 <div key={w.worker_id} className="bg-white rounded-xl p-4 border border-neutral-100 shadow-sm">
 <div className="flex items-center justify-between gap-3">
 <div className="flex items-center gap-3 flex-1 min-w-0">
 <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center font-bold text-brand-700">
 {(w.name || '?')[0].toUpperCase()}
 </div>
 <div className="min-w-0">
 <div className="flex items-center gap-1.5 flex-wrap">
 <p className="font-bold text-neutral-900 text-sm">{w.name}</p>
 <span className={`text-[9px] px-1.5 py-0.5 rounded-full border font-bold ${badge.cls}`}>{badge.label}</span>
 {w.available_now && <span className="text-[9px] px-1.5 py-0.5 bg-brand-50 text-brand-600 rounded-full border border-brand-200"> Online</span>}
 </div>
 <p className="text-[10px] text-neutral-400 mt-0.5">
 {w.completed_shifts || 0} shifts · {w.certs || 'No certs'}
 {w.my_rating && ` · You rated: ${w.my_rating}`}
 </p>
 </div>
 </div>
 <div className="flex flex-col gap-1 shrink-0">
 <select value={w.tier} onChange={e => updateTier(w.worker_id, e.target.value)}
 className="text-[10px] px-2 py-1 border border-neutral-200 rounded-lg bg-white text-neutral-600">
 <option value="trusted">✅ Trusted</option>
 <option value="preferred"> Preferred</option>
 <option value="top_pick"> Top Pick</option>
 </select>
 <button onClick={() => removeWorker(w.worker_id)}
 className="text-[10px] text-red-400 hover:text-red-600 font-medium">Remove</button>
 </div>
 </div>
 </div>
 );
 })}
 </div>
 )}
 </div>

 <div className="bg-neutral-50 rounded-xl p-4">
 <h3 className="font-bold text-neutral-900 text-xs mb-2"> How it works</h3>
 <ol className="text-[10px] text-neutral-500 space-y-1.5">
 <li className="flex gap-2"><span className="text-brand-500 font-bold">1.</span> Hire a worker and they do great? Add them to your crew</li>
 <li className="flex gap-2"><span className="text-brand-500 font-bold">2.</span> Rank them: Trusted → Preferred → Top Pick</li>
 <li className="flex gap-2"><span className="text-brand-500 font-bold">3.</span> Post an urgent shift → offer to crew first</li>
 <li className="flex gap-2"><span className="text-brand-500 font-bold">4.</span> They get a private notification + 2hrs to accept</li>
 <li className="flex gap-2"><span className="text-brand-500 font-bold">5.</span> If accepted → auto-hired instantly</li>
 <li className="flex gap-2"><span className="text-brand-500 font-bold">6.</span> If no one accepts → shift goes public</li>
 </ol>
 </div>
 </div>
 );
}
